import React, { FC, useState, useMemo } from 'react';
import { Switch, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { compose } from 'react-recompose';

import { TabsPanel, Container, PrimaryButton } from '@dev/ee-components';

import StatisticsInterface from './SuppressionsInterface';
import Suppressions from './Suppressions';
import Unsubscribed from './Unsubscribed';
import { SuppressionsPaths } from '@api/services/dictionaries/paths';
import { useMobile } from '@api/services/hooks/useMobile';
import { getAccessLevels } from '@api/store/account/actions';
import { AccessLevel } from '@dev/ee-api/ee-api-models';

const dispatchProps = { Push: push };

type DispatchProps = {
    Push(url: string): void;
};

type Props = DispatchProps & StatisticsInterface;

const getActiveTab = (path: string): number => {
    if (path === SuppressionsPaths.UnsubscribedSuppressions) {
        return 1;
    }
    return 0;
};

const SuppressionsTabs: FC<Props> = (props: Props): JSX.Element => {
    const { t, match, Push } = props;
    const accessLevels = useSelector(getAccessLevels, shallowEqual);
    const mobilePadding = {
        left: 16,
        right: 16,
    };
    const isMobile = useMobile();

    const [tabValue, setTabValue] = useState<number>(getActiveTab(match.path));

    const tabsOnClick = (ev, newValue: number): void => {
        setTabValue(newValue);

        if (newValue === 1) {
            Push(SuppressionsPaths.UnsubscribedSuppressions);
        } else {
            Push(SuppressionsPaths.Suppressions);
        }
    };

    const tabActionComponent = useMemo((): JSX.Element | null => {
        const hasRequiredAccess = accessLevels?.includes(AccessLevel.ModifySuppressions);
        if (tabValue === 0 && accessLevels && hasRequiredAccess) {
            return (
                <PrimaryButton
                    text={t('addsuppressions')}
                    onClick={(): void => {
                        Push(SuppressionsPaths.SuppressionsAdd);
                    }}
                />
            );
        }
        return null;
    }, [tabValue]);

    return (
        <Container paddingMobile={mobilePadding}>
            <TabsPanel
                title={t('suppressions')}
                tabs={[{ label: t('overview') }, { label: t('unsubscribedtab') }]}
                onChange={tabsOnClick}
                value={tabValue}
                isMobile={isMobile}
                additionalHeaderContent={tabActionComponent}
            />
            <Switch>
                <Route path={SuppressionsPaths.Suppressions} exact component={Suppressions} />
                <Route path={SuppressionsPaths.UnsubscribedSuppressions} exact component={Unsubscribed} />
            </Switch>
        </Container>
    );
};

export default compose(withTranslation(), connect<StatisticsInterface, DispatchProps>(null, dispatchProps))(SuppressionsTabs);
